import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from './LoadingSpinner'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: 'consumer' | 'business' | 'admin'
  requiredRoles?: ('consumer' | 'business' | 'admin')[]
  fallbackPath?: string
  redirectTo?: string
}

export default function ProtectedRoute({ 
  children, 
  requiredRole,
  requiredRoles,
  fallbackPath = '/login',
  redirectTo
}: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, isInitialized, hasRole, hasAnyRole } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    // If redirectTo is specified, use it
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />
    }
    
    // Otherwise, redirect based on user role
    const roleBasedRedirect = {
      consumer: '/dashboard',
      business: '/business-dashboard',
      admin: '/admin-dashboard'
    }
    
    return <Navigate to={roleBasedRedirect[user.role] || '/dashboard'} replace />
  }

  // Check multiple roles access
  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />
    }
    
    const roleBasedRedirect = {
      consumer: '/dashboard',
      business: '/business-dashboard',
      admin: '/admin-dashboard'
    }
    
    return <Navigate to={roleBasedRedirect[user.role] || '/dashboard'} replace />
  }

  return <>{children}</>
}

// Higher-order component for protected routes
export function withProtectedRoute<P extends object>(
  Component: React.ComponentType<P>,
  requiredRole?: 'consumer' | 'business' | 'admin',
  fallbackPath?: string
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute requiredRole={requiredRole} fallbackPath={fallbackPath}>
        <Component {...props} />
      </ProtectedRoute>
    )
  }
}
